/**
 * Utility functions for parsing and extracting content from messages
 * Handles JSON-formatted messages from Communication Infrastructure
 */

export interface ParsedMessageContent {
  content: string;
  isJson: boolean;
  hasStructuredData: boolean;
  structuredData?: {
    type?: string;
    charts?: any[];
    insights?: any[];
    statistics?: any;
    source_data?: any;
  };
}

const EMPTY_DATA_FALLBACK_TEXT = 'I found no matching SAP data for this question. Try changing the period, removing one of the filters, or asking about a broader metric.';
const STRUCTURED_NO_TEXT_FALLBACK_TEXT = 'The analysis completed, but there was no displayable answer text.';

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function fallbackTextFromDataGaps(payload: Record<string, any>): string | undefined {
  const blocks = Array.isArray(payload.blocks) ? payload.blocks : [];
  const dataGapBlock = blocks.find((block: unknown) => (
    isRecord(block) && String(block.type || '').toLowerCase() === 'data_gaps'
  ));
  const items = Array.isArray(dataGapBlock?.data?.items) ? dataGapBlock.data.items : [];
  if (!items.length) return undefined;

  const query = firstNonEmptyString(payload.task, payload.query, payload.meta?.raw_question, 'this question') || 'this question';
  const quotedQuery = query === 'this question' ? query : `"${query.replace(/^"+|"+$/g, '')}"`;
  const searchableText = items
    .map((item: unknown) => isRecord(item) ? [item.impact, item.summary, item.concept].filter(Boolean).join(' ') : '')
    .join(' ')
    .toLowerCase();

  const chartDropped = searchableText.includes('chart_dropped');
  const kpiDropped = searchableText.includes('kpi_card_dropped');

  if (chartDropped && kpiDropped) {
    return `I could not build a chart or KPI for ${quotedQuery}. The data pipeline did not return renderable results for the requested view. Try rerunning the question, or check the underlying tool output.`;
  }
  if (chartDropped) {
    return `I could not build the chart for ${quotedQuery}. The data pipeline did not return renderable chart data. Try rerunning the question, or check the underlying tool output.`;
  }
  if (kpiDropped) {
    return `I could not build the KPI for ${quotedQuery}. The data pipeline did not return renderable KPI data. Try rerunning the question, or check the underlying tool output.`;
  }
  return `I could not fully answer ${quotedQuery}. The response contains data gaps, but no displayable answer text.`;
}

export function displayTextFromStructuredPayload(payload: unknown): string | undefined {
  if (!isRecord(payload)) return undefined;

  const directText = firstNonEmptyString(
    payload.normal_text,
    payload.summary,
    payload.reply,
    payload.response,
    payload.data?.normal_text,
    payload.data?.response,
  );
  if (directText) return directText;

  if (payload.meta?.empty_data_short_circuit) return EMPTY_DATA_FALLBACK_TEXT;

  if (payload.$schema === 'smartchg-chat/v1') {
    return fallbackTextFromDataGaps(payload) || STRUCTURED_NO_TEXT_FALLBACK_TEXT;
  }

  return undefined;
}

/**
 * Parse message content - handles both plain text, JSON strings, and objects
 * Backend unwraps Smart Insights responses and may return content as an object.
 * This function extracts the actual response content, prioritizing 'response' over 'message'
 */
export function parseMessageContent(messageContent: string | object): ParsedMessageContent {
  // Handle null/undefined
  if (!messageContent) {
    return {
      content: '',
      isJson: false,
      hasStructuredData: false,
    };
  }

  // If content is already an object (unwrapped by backend), process it directly
  if (typeof messageContent === 'object' && !Array.isArray(messageContent)) {
    const parsed = messageContent as any;
    const displayText = displayTextFromStructuredPayload(parsed);
    if (displayText) {
      return {
        content: displayText,
        isJson: true,
        hasStructuredData: parsed.$schema === 'smartchg-chat/v1' || Boolean(parsed.type === 'complete' && parsed.data),
      };
    }
    
    // Check if it's a structured message with data (Smart Insights format)
    if (parsed.type === 'complete' && parsed.data) {
      const data = parsed.data;
      let content = '';
      
      // Extract text content - prioritize normal_text, then response, then message
      if (data.normal_text) {
        content = typeof data.normal_text === 'string' ? data.normal_text : String(data.normal_text);
      } else if (data.response) {
        content = typeof data.response === 'string' ? data.response : String(data.response);
      } else if (data.content) {
        content = typeof data.content === 'string' ? data.content : String(data.content);
      } else if (data.message) {
        content = typeof data.message === 'string' ? data.message : String(data.message);
      } else {
        // Create a summary for structured data
        const parts: string[] = [];
        if (data.charts && Array.isArray(data.charts) && data.charts.length > 0) {
          parts.push(`${data.charts.length} chart${data.charts.length > 1 ? 's' : ''}`);
        }
        if (data.insights && Array.isArray(data.insights) && data.insights.length > 0) {
          parts.push(`${data.insights.length} insight${data.insights.length > 1 ? 's' : ''}`);
        }
        if (data.statistics) {
          parts.push('statistics');
        }
        if (data.source_data && typeof data.source_data === 'object') {
          const tableCount = Object.keys(data.source_data).length;
          if (tableCount > 0) {
            parts.push(`${tableCount} data table${tableCount > 1 ? 's' : ''}`);
          }
        }
        
        if (parts.length > 0) {
          content = `Response includes: ${parts.join(', ')}`;
        } else {
          content = 'Structured response with data';
        }
      }
      
      return {
        content,
        isJson: true,
        hasStructuredData: true,
        structuredData: {
          type: parsed.type,
          charts: data.charts,
          insights: data.insights,
          statistics: data.statistics,
          source_data: data.source_data,
        },
      };
    }
    
    // Handle older persisted response-shaped payloads.
    // Backend wraps this as: { success, message, response, chunks_used, ... }
    // We want the 'response' field, NOT the 'message' field
    if (parsed.normal_text) {
      return {
        content: typeof parsed.normal_text === 'string' ? parsed.normal_text : String(parsed.normal_text),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    if (parsed.response) {
      return {
        content: typeof parsed.response === 'string' ? parsed.response : String(parsed.response),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    // Fallback to other fields if response is not available
    if (parsed.content) {
      return {
        content: typeof parsed.content === 'string' ? parsed.content : String(parsed.content),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    if (parsed.data && parsed.data.normal_text) {
      return {
        content: typeof parsed.data.normal_text === 'string' ? parsed.data.normal_text : String(parsed.data.normal_text),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    if (parsed.data && parsed.data.response) {
      return {
        content: typeof parsed.data.response === 'string' ? parsed.data.response : String(parsed.data.response),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    if (parsed.data && parsed.data.content) {
      return {
        content: typeof parsed.data.content === 'string' ? parsed.data.content : String(parsed.data.content),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    // Only use 'message' as last resort (it's usually a status message, not the actual content)
    if (parsed.message) {
      return {
        content: typeof parsed.message === 'string' ? parsed.message : String(parsed.message),
        isJson: true,
        hasStructuredData: false,
      };
    }
    
    // Fallback: stringify the object
    return {
      content: JSON.stringify(parsed, null, 2),
      isJson: true,
      hasStructuredData: false,
    };
  }

  // If content is a string, try to parse as JSON
  if (typeof messageContent !== 'string') {
    return {
      content: String(messageContent),
      isJson: false,
      hasStructuredData: false,
    };
  }

  // Try to parse as JSON string
  try {
    const parsed = JSON.parse(messageContent);
    
    if (parsed && typeof parsed === 'object') {
      const displayText = displayTextFromStructuredPayload(parsed);
      if (displayText) {
        return {
          content: displayText,
          isJson: true,
          hasStructuredData: parsed.$schema === 'smartchg-chat/v1' || Boolean(parsed.type === 'complete' && parsed.data),
        };
      }

      // Check if it's a structured message with data
      if (parsed.type === 'complete' && parsed.data) {
        const data = parsed.data;
        let content = '';
        
        // Extract text content - prioritize normal_text, then response, then message
        if (data.normal_text) {
          content = typeof data.normal_text === 'string' ? data.normal_text : String(data.normal_text);
        } else if (data.response) {
          content = typeof data.response === 'string' ? data.response : String(data.response);
        } else if (data.content) {
          content = typeof data.content === 'string' ? data.content : String(data.content);
        } else if (data.message) {
          content = typeof data.message === 'string' ? data.message : String(data.message);
        } else {
          // Create a summary for structured data
          const parts: string[] = [];
          if (data.charts && Array.isArray(data.charts) && data.charts.length > 0) {
            parts.push(`${data.charts.length} chart${data.charts.length > 1 ? 's' : ''}`);
          }
          if (data.insights && Array.isArray(data.insights) && data.insights.length > 0) {
            parts.push(`${data.insights.length} insight${data.insights.length > 1 ? 's' : ''}`);
          }
          if (data.statistics) {
            parts.push('statistics');
          }
          if (data.source_data && typeof data.source_data === 'object') {
            const tableCount = Object.keys(data.source_data).length;
            if (tableCount > 0) {
              parts.push(`${tableCount} data table${tableCount > 1 ? 's' : ''}`);
            }
          }
          
          if (parts.length > 0) {
            content = `Response includes: ${parts.join(', ')}`;
          } else {
            content = 'Structured response with data';
          }
        }
        
        return {
          content,
          isJson: true,
          hasStructuredData: true,
          structuredData: {
            type: parsed.type,
            charts: data.charts,
            insights: data.insights,
            statistics: data.statistics,
            source_data: data.source_data,
          },
        };
      }
      
      // Handle normal_text payloads first, then 'response' over 'message'.
      if (parsed.normal_text) {
        return {
          content: typeof parsed.normal_text === 'string' ? parsed.normal_text : String(parsed.normal_text),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      if (parsed.response) {
        return {
          content: typeof parsed.response === 'string' ? parsed.response : String(parsed.response),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      // Fallback to other fields
      if (parsed.content) {
        return {
          content: typeof parsed.content === 'string' ? parsed.content : String(parsed.content),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      if (parsed.data && parsed.data.response) {
        return {
          content: typeof parsed.data.response === 'string' ? parsed.data.response : String(parsed.data.response),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      if (parsed.data && parsed.data.content) {
        return {
          content: typeof parsed.data.content === 'string' ? parsed.data.content : String(parsed.data.content),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      // Only use 'message' as last resort
      if (parsed.message) {
        return {
          content: typeof parsed.message === 'string' ? parsed.message : String(parsed.message),
          isJson: true,
          hasStructuredData: false,
        };
      }
      
      // Fallback: stringify the JSON
      return {
        content: JSON.stringify(parsed, null, 2),
        isJson: true,
        hasStructuredData: false,
      };
    }
  } catch (e) {
    // Not JSON, return as-is
  }
  
  // Not JSON or parsing failed, return original content
  return {
    content: messageContent,
    isJson: false,
    hasStructuredData: false,
  };
}

/**
 * Extract readable preview from message content
 */
export function getMessagePreview(content: string, maxLength: number = 80): string {
  const parsed = parseMessageContent(content);
  const preview = parsed.content.length > maxLength
    ? parsed.content.substring(0, maxLength) + '...'
    : parsed.content;
  return preview;
}
